import { memo, useCallback, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import compact from 'lodash/compact';

import TreeOption from './TreeOption';
import { initMenu, updateMenu } from './functions';

import './TreeMenu.css';

export const renderIcon = (expanded) => (
  <span>
    <i
      className={classnames([
        'fa',
        `fa-caret-${expanded ? 'down' : 'right'}`,
        'margin-right',
        'blue-400',
        'tree-menu__label-icon',
      ])}
    />
    <i
      className={classnames([
        'fa',
        `fa-folder-${expanded ? 'open-o' : 'o'}`,
        'margin-right',
        'blue-400',
        'tree-menu__label-icon',
      ])}
    />
  </span>
);

const renderChildOption = (option, props, state) => {
  const {
    disabled,
    pathFromRoot,
    selectedOptions,
    onChange,
    getOptionId,
    renderOption,
    getOptionLabel,
    getOptionDescription,
  } = props;

  const { parentExpanded } = state;

  const key = `option-${getOptionId(option)}`;

  const id = getOptionId(option);

  const selectedOptionIds = selectedOptions?.map(getOptionId) || [];

  const checked = selectedOptionIds.includes(id);

  const childOption =
    typeof renderOption === 'function' ? (
      renderOption(option, props, state)
    ) : (
      <TreeOption
        key={key}
        option={option}
        checked={checked}
        visible={parentExpanded && option.visible}
        disabled={disabled}
        selectedOptions={selectedOptions}
        // @ts-expect-error
        pathFromRoot={pathFromRoot}
        onChange={onChange}
        getOptionId={getOptionId}
        getOptionLabel={getOptionLabel}
        getOptionDescription={getOptionDescription}
      />
    );

  return childOption;
};

const renderChildSubmenu = (menu, props, { parentExpanded }) => {
  const {
    disabled,
    pathFromRoot,
    isOption,
    isSkipped,
    getMenuId,
    isExpanded,
    getOptionId,
    selectedOptions,
    showOnlySelected,
    showOnlyNonEmpty,
    getMenuLabel,
    getOptionLabel,
    getOptionDescription,
    renderOption,
    renderMenuLabel,
    onChange,
    onExpandGroup,
  } = props;

  const menuId = getMenuId(menu);

  const key = `submenu-${menuId}`;

  const nextPathFromRoot = compact([pathFromRoot, `${menuId}`]).join('|');

  if (!parentExpanded || !menu.visible) {
    return null;
  }

  return (
    <TreeMenu
      key={key}
      menu={menu}
      disabled={disabled}
      // @ts-expect-error
      pathFromRoot={nextPathFromRoot}
      selectedOptions={selectedOptions}
      showOnlySelected={showOnlySelected}
      showOnlyNonEmpty={showOnlyNonEmpty}
      isOption={isOption}
      isSkipped={isSkipped}
      getMenuId={getMenuId}
      isExpanded={isExpanded}
      getOptionId={getOptionId}
      getMenuLabel={getMenuLabel}
      renderOption={renderOption}
      getOptionLabel={getOptionLabel}
      getOptionDescription={getOptionDescription}
      renderMenuLabel={renderMenuLabel}
      onChange={onChange}
      onExpandGroup={onExpandGroup}
    />
  );
};

const TreeMenu = (props) => {
  const {
    menu,
    disabled,
    selectedOptions,
    showOnlySelected,
    showOnlyNonEmpty,
    getMenuId,
    isOption,
    isSkipped,
    isExpanded,
    getOptionId,
    getMenuLabel,
    renderMenuLabel,
    onExpandGroup,
  } = props;

  const [updatedMenu, setUpdatedMenu] = useState(menu);

  useEffect(() => {
    setUpdatedMenu(
      initMenu(
        menu,
        selectedOptions,
        {
          isOption,
          getMenuId,
          getOptionId,
          showOnlySelected,
          showOnlyNonEmpty,
        },
        // @ts-expect-error
        menu.parentPath,
      ),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setUpdatedMenu(
      updateMenu(menu, selectedOptions, {
        isOption,
        getOptionId,
        showOnlySelected,
        showOnlyNonEmpty,
      }),
    );
  }, [disabled, menu, selectedOptions, showOnlySelected, showOnlyNonEmpty, isOption, getOptionId]);

  const handleExpand = useCallback(() => {
    onExpandGroup(updatedMenu);
  }, [updatedMenu, onExpandGroup]);

  if (!updatedMenu) {
    return null;
  }

  const expanded = isExpanded(updatedMenu);

  const { children } = updatedMenu;

  if (isSkipped(updatedMenu)) {
    return (
      <div className="tree-menu-wrapper">
        {children?.map((child) =>
          isOption(child)
            ? renderChildOption(child, props, { parentExpanded: true })
            : renderChildSubmenu(child, props, { parentExpanded: true }),
        )}
      </div>
    );
  }

  const labelInner =
    typeof renderMenuLabel === 'function' ? (
      renderMenuLabel(updatedMenu, props, { parentExpanded: expanded })
    ) : (
      <div className="tree-menu__label-inner">
        {renderIcon(expanded)}
        {getMenuLabel(updatedMenu)}
        {expanded
          ? null
          : updatedMenu.selectedCount > 0 && (
              <div className="badge bg-primary margin-left">{updatedMenu.selectedCount}</div>
            )}
      </div>
    );

  const label = (
    // @ts-expect-error
    <div role="button" tabIndex="0" className="tree-menu__label" onClick={handleExpand}>
      {labelInner}
    </div>
  );

  return (
    <div className="tree-menu">
      {label}
      {children?.map((child) =>
        isOption(child)
          ? renderChildOption(child, props, { parentExpanded: expanded })
          : renderChildSubmenu(child, props, { parentExpanded: expanded }),
      )}
    </div>
  );
};

export const TreeMenuPropTypes = {
  menu: PropTypes.object,
  selectedOptions: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.object, PropTypes.number, PropTypes.string])),
    PropTypes.object,
  ]),
  disabled: PropTypes.bool,
  showOnlySelected: PropTypes.bool,
  showOnlyNonEmpty: PropTypes.bool,
  isSkipped: PropTypes.func,
  isOption: PropTypes.func.isRequired,
  isExpanded: PropTypes.func.isRequired,
  onChange: PropTypes.func.isRequired,
  onExpandGroup: PropTypes.func.isRequired,
  getMenuId: PropTypes.func.isRequired,
  getOptionId: PropTypes.func,
  getMenuLabel: PropTypes.func,
  renderMenuLabel: PropTypes.func,
  getOptionLabel: PropTypes.func,
  getOptionDescription: PropTypes.func,
  renderOption: PropTypes.func,
};

TreeMenu.propTypes = TreeMenuPropTypes;

// eslint-disable-next-line import/no-default-export
export default memo(TreeMenu);
